import React from 'react'
import { IconExternalLink } from '@tabler/icons-react'
import { useAppStore } from '../store'
import { COLORMAPS } from '../../../components/ZarrMapViewer/colormaps'
import { buildExplorerSearch, readViewport } from '../urlState'

/**
 * Shared card treatment for all floating map chrome: dataset chip, colorbar
 * legend, time bar, dataset-switch notice. Same visual family so nothing
 * reads as bolt-on.
 */
export const mapCardStyle: React.CSSProperties = {
  position: 'absolute',
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid var(--rule)',
  borderRadius: 6,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  zIndex: 90,
}

const cardStyle = mapCardStyle

const QA_LABELS: Record<number, string> = { 0: '≥0.5', 1: '≥0.75', 2: '≥1.0' }

/**
 * Top-left: current-view anchor chip.
 * Composes `<variable> · <collection name>[ · QA ≥x][ · mean of N steps]`.
 * The chip changes to a mean-state pill when a time-mean is displayed so a
 * mean can't be mistaken for a single frame.
 */
export const DatasetChip = () => {
  const sidebarWidth = useAppStore((s) => s.sidebarWidth) ?? 0
  const datasetModule = useAppStore((s) => s.getDatasetModule())
  const datasetState = useAppStore((s) => s.datasetState) as Record<string, unknown>
  const timeMeanResult = useAppStore((s) => s.timeMeanResult)
  const timeMeanStartDate = useAppStore((s) => s.timeMeanStartDate)
  const timeMeanEndDate = useAppStore((s) => s.timeMeanEndDate)
  const reverseTimeIndex = useAppStore((s) => s.reverseTimeIndex)
  const storeTimeCount = useAppStore((s) => s.timeCount)
  const zarrLayer = useAppStore((s) => s.zarrLayer)
  const setTimeMeanResult = useAppStore((s) => s.setTimeMeanResult)
  const setTimeMeanEnabled = useAppStore((s) => s.setTimeMeanEnabled)

  if (!datasetModule) return null

  // Pull whichever state key represents "the variable" for this dataset.
  const variableToken =
    (typeof datasetState.variable === 'string' && datasetState.variable) ||
    (typeof datasetState.group === 'string' && datasetState.group) ||
    (typeof datasetState.sensor === 'string' && datasetState.sensor) ||
    undefined
  const qa =
    typeof datasetState.qaIndex === 'number'
      ? QA_LABELS[datasetState.qaIndex as number]
      : undefined

  const isMean = timeMeanResult !== null
  // Compute step count from brush dates — same logic as TimeSlider's windowSteps.
  // (timeMeanResult.data.length is spatial pixels, not time steps.)
  const meanSteps = (() => {
    if (!isMean || !timeMeanStartDate || !timeMeanEndDate || !reverseTimeIndex) return undefined
    const max = Math.max(0, storeTimeCount - 1)
    const lo = Math.max(0, Math.min(max, Math.round(reverseTimeIndex(timeMeanStartDate))))
    const hi = Math.max(0, Math.min(max, Math.round(reverseTimeIndex(timeMeanEndDate))))
    return Math.max(1, hi - lo + 1)
  })()

  const clearMean = () => {
    ;(zarrLayer as any)?.setTimeMeanData?.(null)
    setTimeMeanResult(null)
    setTimeMeanEnabled(false)
  }

  const parts = [
    variableToken && (
      <span
        key="var"
        style={{ fontFamily: 'var(--card-mono)', fontWeight: 600 }}
      >
        {variableToken}
      </span>
    ),
    <span key="ds">{datasetModule.info}</span>,
    qa && <span key="qa" style={{ color: 'var(--slate)' }}>QA {qa}</span>,
    isMean && meanSteps && (
      <span
        key="mean"
        style={{
          fontFamily: 'var(--card-mono)',
          color: 'var(--teal)',
          fontWeight: 600,
        }}
      >
        mean of {meanSteps} steps
      </span>
    ),
  ].filter(Boolean) as React.ReactNode[]

  return (
    <div
      style={{
        ...cardStyle,
        top: 10,
        left: sidebarWidth + 10,
        padding: '4px 10px',
        fontFamily: 'var(--card-sans)',
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--navy)',
        maxWidth: 360,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderLeft: isMean ? '3px solid var(--teal)' : undefined,
      }}
    >
      {parts.map((el, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 && <span style={{ color: 'var(--grey)', margin: '0 6px' }}>·</span>}
          {el}
        </span>
      ))}
      {isMean && (
        <button
          onClick={clearMean}
          title="Exit mean display"
          aria-label="Exit mean display"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--grey)',
            fontSize: 14,
            lineHeight: 1,
            padding: '0 0 0 6px',
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}

/**
 * Bottom-left: colormap legend with min/max labels + optional units.
 * Sits above any TimeSeriesChartOverlay by offsetting when it's present.
 */
export const MapColorbar = () => {
  const sidebarWidth = useAppStore((s) => s.sidebarWidth) ?? 0
  const clim = useAppStore((s) => s.clim)
  const colormap = useAppStore((s) => s.colormap)
  const timeSliderHeight = useAppStore((s) => s.timeSliderHeight)
  const timeCount = useAppStore((s) => s.timeCount)
  const datasetModule = useAppStore((s) => s.getDatasetModule())
  const datasetState = useAppStore((s) => s.datasetState)
  const colors = COLORMAPS[colormap] ?? COLORMAPS['eodc']
  const units =
    datasetModule?.buildLayerProps?.(datasetState)?.units ??
    (datasetModule as unknown as { units?: string })?.units

  const hasTimeSlider = timeCount > 1
  const effectiveSliderHeight = hasTimeSlider ? (timeSliderHeight || 44) : 0
  // Sit just above the time slider. zIndex 90 > panel zIndex 80 keeps the
  // colorbar visible on top even when the time-series chart is open.
  const baseBottom = 10 + effectiveSliderHeight + 8

  return (
    <div
      style={{
        ...cardStyle,
        left: sidebarWidth + 10,
        bottom: baseBottom,
        padding: '6px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 3,
      }}
    >
      {/* Top row: min — bar — max */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            fontFamily: 'var(--card-mono)',
            fontSize: 10,
            color: 'var(--slate)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 28,
            textAlign: 'right',
          }}
        >
          {formatTick(clim[0])}
        </span>
        <div
          style={{
            flex: 1,
            minWidth: 100,
            height: 8,
            borderRadius: 2,
            background: `linear-gradient(to right, ${colors.join(',')})`,
          }}
        />
        <span
          style={{
            fontFamily: 'var(--card-mono)',
            fontSize: 10,
            color: 'var(--slate)',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 28,
          }}
        >
          {formatTick(clim[1])}
        </span>
      </div>
      {/* Unit label centred below the bar */}
      {units && (
        <div
          style={{
            textAlign: 'center',
            fontFamily: 'var(--card-mono)',
            fontSize: 9,
            color: 'var(--grey)',
          }}
        >
          {units}
        </div>
      )}
    </div>
  )
}

function formatTick(v: number): string {
  if (!Number.isFinite(v)) return '—'
  const abs = Math.abs(v)
  if (abs >= 100) return v.toFixed(0)
  if (abs >= 10) return v.toFixed(1)
  if (abs >= 1) return v.toFixed(2)
  return v.toPrecision(2)
}

/**
 * Top-right (collection mode only): opens the viewer in a new window
 * in `full` mode with current state carried via URL params. Uses a
 * named window target so repeat clicks reuse the same window.
 */
export const OpenInNewWindowButton = () => {
  const scope = useAppStore((s) => s.scope)
  const rightRailWidth = useAppStore((s) => s.rightRailWidth)
  const datasetId = useAppStore((s) => s.datasetId)
  const datasetState = useAppStore((s) => s.datasetState) as Record<string, unknown>
  const clim = useAppStore((s) => s.clim)
  const colormap = useAppStore((s) => s.colormap)
  const opacity = useAppStore((s) => s.opacity)
  const timeMeanAutoClim = useAppStore((s) => s.timeMeanAutoClim)
  const mapInstance = useAppStore((s) => s.mapInstance)

  if (scope !== 'collection') return null

  const openFull = () => {
    const { center, zoom } = readViewport(mapInstance)
    const q = buildExplorerSearch({
      collection: datasetId,
      variable: typeof datasetState.variable === 'string' ? datasetState.variable : undefined,
      qa: typeof datasetState.qaIndex === 'number' ? String(datasetState.qaIndex) : undefined,
      cmap: colormap,
      vmin: clim[0],
      vmax: clim[1],
      autoscale: timeMeanAutoClim,
      opacity,
      t: typeof datasetState.time === 'number' ? datasetState.time : undefined,
      center,
      zoom,
    })
    const url = `${window.location.origin}/zarr-explorer?${q}`
    window.open(url, 'eodc-zarr-viewer')
  }

  return (
    <button
      onClick={openFull}
      title="Open in a new window"
      style={{
        position: 'absolute',
        top: 10,
        right: rightRailWidth + 10,
        zIndex: 100,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 29,
        padding: '0 10px',
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: 4,
        cursor: 'pointer',
        fontFamily: 'var(--card-sans)',
        fontSize: 11,
        fontWeight: 500,
        color: 'var(--navy)',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.06)',
      }}
    >
      <IconExternalLink size={13} stroke={2} />
      Open in new window
    </button>
  )
}
